// Client for release-service — Phase D release hub (Apps / Builds / Rollout).
// Mirrors staffApi.ts / academyApi.ts conventions exactly: same shared
// `platform` cluster host resolution, same auth header source.
//
// NOTE the host shape: release-service lives on the SHARED `platform`
// cluster and is addressed by BASE PATH (/release), not a per-service
// subdomain. Do not "correct" this to release.api.<apex> — that pattern is
// closed (AWS domain cap, see feedback_resource_limits.md).
//
// Response shapes come from @platform/shared-types (releases.ts) — never
// re-declared here. See docs/superpowers/plans/2026-09-02-phase-d-release-hub.md,
// Task 3, and .superpowers/sdd/2026-09-02-phase-d-release-hub/task-D2-report.md
// for the exact shape and the seam trace that produced it.
import type {
  ListAppsResponse, BuildRecord, SetBuildLabelRequest, ReleaseEnv, ReleaseRing,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveReleaseHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/release-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/release`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/release`;
  return `https://platform.api.dev.${apex}/release`;
}

/** Exported in case a sibling module ever needs the same host. */
export const releaseHost = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_RELEASE_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveReleaseHost(h, detectStage(), envBase);
};

// Same auth source as staffApi.ts / hardwareApi.ts / academyApi.ts / billingApi.ts:
// super-admin-ui stores the admin session token under 'jwtToken' and sends it
// bare (no "Bearer " prefix).
export function releaseAuthHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${releaseHost()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...releaseAuthHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || body?.message || '';
    } catch { /* non-JSON body */ }
    throw new Error(detail || `release ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface ListBuildsResponse {
  items: BuildRecord[];
  count: number;
}

export interface SetBuildLabelResponse {
  updated: true;
  appId: string;
  env: string;
  target: string;
  version: string;
  /** The stored label, or null when it was cleared. */
  label: string | null;
  /** Derived server-side by the same `buildShortId` the UI uses. */
  shortId: string;
}

/**
 * `POST /rings` — move a ring pointer at a build that is ALREADY registered.
 *
 * This is the only call in the platform that makes a till install something.
 * Registering a build does not; labelling one does not. The server writes the
 * `ring#<ring>` row and publishes `ring.promoted` on the release queue, which
 * device-config-service consumes into its own `ring#` row — the row every
 * check-in reads. Until a ring row exists a device is authoritatively told
 * "no target", i.e. never updates.
 *
 * `promotedBy` is NOT in this request and must never be added: the handler
 * takes it from the caller's verified Cognito claims. An actor the caller can
 * set is not an audit trail.
 */
export interface PromoteRingRequest {
  appId: string;
  env: ReleaseEnv;
  target: string;
  ring: ReleaseRing;
  /** Must match a registered build for appId/env/target — the handler 404s otherwise. */
  version: string;
}

export interface PromoteRingResponse {
  promoted: true;
  appId: string;
  env: ReleaseEnv;
  target: string;
  ring: ReleaseRing;
  version: string;
  /** Derived server-side from the caller's claims, never from the request. */
  promotedBy: string;
}

/**
 * Which release env this browser session is talking to, or null when that
 * cannot be stated (staging). Used only to decide whether a device count read
 * from device-config-service — which is stage-scoped — describes the SAME
 * fleet as the env being promoted to. A count from the wrong stage is worse
 * than no count in a blast-radius confirmation.
 */
export function currentReleaseEnv(): ReleaseEnv | null {
  const stage = detectStage();
  return stage === 'prod' ? 'prod' : stage === 'dev' ? 'dev' : null;
}

export const releaseApi = {
  /** GET /apps — one summarised call for the whole hub. Super-admin gated. */
  apps: {
    list: () => call<ListAppsResponse>('/apps'),
  },

  /**
   * GET /builds?appId=&env= — full build history for one app/env pair,
   * newest first (server-side ScanIndexForward:false). Both params are
   * required by the handler (400 without them) — the Builds tab always
   * supplies them, derived from a prior /apps call.
   */
  builds: {
    list: (appId: string, env: string) =>
      call<ListBuildsResponse>(`/builds?appId=${encodeURIComponent(appId)}&env=${encodeURIComponent(env)}`),

    /**
     * PATCH /builds/label — rename a build, or clear its name with `null`.
     *
     * `label` is NULLABLE, never optional: `undefined` would be dropped by
     * JSON.stringify and the server would keep the old label, so clearing has
     * to be an explicit null. See feedback_json_undefined_never_clears_a_field.
     *
     * Cosmetic only — the server does not publish a promotion for a rename, so
     * this can never move a device.
     */
    setLabel: (req: SetBuildLabelRequest) =>
      call<SetBuildLabelResponse>('/builds/label', { method: 'PATCH', body: JSON.stringify(req) }),
  },

  rings: {
    /**
     * POST /rings — super-admin gated (platform-admin Cognito pool, same
     * bearer as every other call here; NOT the internal CI key, which only
     * gates POST /builds).
     *
     * Moves devices. Never call this without an explicit human confirmation
     * that named the ring, the target and the blast radius.
     */
    promote: (req: PromoteRingRequest) =>
      call<PromoteRingResponse>('/rings', { method: 'POST', body: JSON.stringify(req) }),
  },
};
